import React from 'react';
import { motion } from 'framer-motion';
import { getWhatsAppUrl } from '../content/site-info';
import { 
  FaShieldAlt, 
  FaClock, 
  FaCheckCircle, 
  FaMoneyBillWave,
  FaCertificate,
  FaHome
} from 'react-icons/fa';

const WhyChooseUs = () => {
  const features = [
    {
      icon: <FaShieldAlt className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8" />,
      title: "Garantia de Serviço",
      description: "Todos os nossos serviços vêm com garantia completa. Sua tranquilidade é nossa prioridade.",
      color: "from-blue-500 to-blue-600",
      delay: 0.1
    },
    {
      icon: <FaClock className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8" />,
      title: "Atendimento Rápido",
      description: "Agilidade no diagnóstico e execução. Seu tempo é valioso e respeitamos isso.",
      color: "from-green-500 to-green-600",
      delay: 0.2
    },
    {
      icon: <FaCheckCircle className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8" />,
      title: "Peças Originais",
      description: "Trabalhamos apenas com peças originais e de qualidade comprovada.",
      color: "from-purple-500 to-purple-600",
      delay: 0.3
    },
    {
      icon: <FaMoneyBillWave className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8" />,
      title: "Orçamento Grátis",
      description: "Orçamento sem compromisso e totalmente gratuito. Transparência desde o início.",
      color: "from-yellow-500 to-orange-500",
      delay: 0.4
    },
    {
      icon: <FaCertificate className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8" />,
      title: "Técnicos Certificados",
      description: "Equipe altamente qualificada e certificada pelas principais fabricantes.",
      color: "from-red-500 to-pink-500",
      delay: 0.5
    },
    {
      icon: <FaHome className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8" />,
      title: "Atendimento Diferenciado",
      description: "Comodidade em Horário. Atendemos com horários diferenciados e fora do comercial.",
      color: "from-indigo-500 to-indigo-600",
      delay: 0.6
    }
  ];

  return (
    <section className="section-padding surface-section">
      <div className="container-responsive">
        {/* Cabeçalho */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-8 xs:mb-10 sm:mb-12 md:mb-16"
        >
          <h2 className="text-2xl xs:text-3xl xs2:text-3xl xs3:text-4xl sm:text-4xl 
                       md:text-5xl lg:text-6xl font-bold text-gray-800 mb-3 xs:mb-4">
            <span className="text-gradient">Por Que Escolher Nós?</span>
          </h2>
          <p className="text-sm xs:text-base sm:text-lg md:text-xl text-gray-600 
                       max-w-2xl mx-auto px-4">
            Diferenciais que fazem a diferença no atendimento e na qualidade dos nossos serviços
          </p>
          <div className="w-16 xs:w-20 sm:w-24 md:w-32 h-0.5 xs:h-1 bg-primary-600 
                        mx-auto mt-4 xs:mt-5 sm:mt-6 rounded-full"></div>
        </motion.div>

        {/* Grid de Features */}
        <div className="grid grid-cols-1 xs4:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-5 
                       sm:gap-6 md:gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.5,
                delay: feature.delay,
                type: "spring",
                stiffness: 100
              }}
              viewport={{ once: true, margin: "-50px" }}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3 }
              }}
              className="card-modern group relative overflow-hidden"
            >
              {/* Gradiente de fundo no hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} 
                             opacity-0 group-hover:opacity-5 transition-opacity 
                             duration-300`}></div>

              {/* Ícone com gradiente */}
              <motion.div
                className={`w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 mx-auto mb-4 
                          xs:mb-5 sm:mb-6 flex items-center justify-center rounded-2xl
                          bg-gradient-to-br ${feature.color} text-white
                          shadow-lg group-hover:shadow-xl
                          transition-all duration-300 relative z-10`}
                whileHover={{ 
                  scale: 1.15,
                  rotate: [0, -5, 5, 0],
                  transition: { duration: 0.4 }
                }}
              >
                {feature.icon}
                
                {/* Efeito de brilho no hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} 
                               opacity-0 group-hover:opacity-20 rounded-2xl blur-xl 
                               transition-opacity duration-300`}></div>
              </motion.div>

              {/* Título */}
              <h3 className="text-lg xs:text-xl sm:text-2xl font-bold text-gray-800 
                           mb-2 xs:mb-3 text-center group-hover:text-primary-700 
                           transition-colors duration-300">
                {feature.title}
              </h3>

              {/* Descrição */}
              <p className="text-sm xs:text-base sm:text-lg text-gray-600 text-center 
                          leading-relaxed">
                {feature.description}
              </p>

              {/* Linha decorativa */}
              <div className={`mt-4 xs:mt-5 h-1 w-20 mx-auto rounded-full 
                             bg-gradient-to-r ${feature.color} opacity-0 
                             group-hover:opacity-100 transition-opacity duration-300`}></div>

              {/* Badge decorativo */}
              <motion.div
                className={`absolute top-4 right-4 w-3 h-3 rounded-full 
                          bg-gradient-to-br ${feature.color} opacity-0 
                          group-hover:opacity-100 transition-opacity duration-300`}
                whileHover={{ scale: 1.5 }}
              ></motion.div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          viewport={{ once: true }}
          className="mt-12 xs:mt-14 sm:mt-16 md:mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 
                        rounded-2xl p-6 xs:p-8 sm:p-10 md:p-12 border border-primary-200">
            <h3 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-bold 
                         text-gray-800 mb-3 xs:mb-4">
              Pronto para Experimentar Nossos Serviços?
            </h3>
            <p className="text-sm xs:text-base sm:text-lg text-gray-600 mb-6 xs:mb-8 
                         max-w-2xl mx-auto">
              Entre em contato conosco e descubra por que somos a melhor escolha 
              para seus equipamentos eletrônicos
            </p>
            <div className="flex flex-col xs4:flex-row gap-3 xs:gap-4 justify-center 
                          items-center">
              <motion.a
                href="#formularios-combinados"
                className="btn-primary text-sm xs:text-base sm:text-lg px-6 xs:px-8 
                          sm:px-10 py-3 xs:py-3.5 sm:py-4 w-full xs4:w-auto 
                          min-h-12 inline-flex items-center justify-center whitespace-nowrap"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                Solicitar Orçamento
              </motion.a>
              <motion.a
                href={getWhatsAppUrl('Olá! Quero saber mais sobre os serviços da Notemaster.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm xs:text-base sm:text-lg px-6 xs:px-8 
                          sm:px-10 py-3 xs:py-3.5 sm:py-4 w-full xs4:w-auto 
                          min-h-12 inline-flex items-center justify-center whitespace-nowrap"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                Falar no WhatsApp
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;

